/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { TeamPlayerFinderRegistration } from '@sportninja/common/features/team/playerFinder/types'
import { playerFinderRegistrationLabel } from '@sportninja/common/features/team/playerFinder/utils/registrationLabel'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import Text from 'src/components/ts-components/Text'
import { SCOPE_CONTROL_HEIGHT } from 'src/features/team-balance/constants/registrationScopeRow'
import { balanceText } from 'src/features/team-balance/utils/balanceI18n'

interface PlayerFinderRegistrationPickerProps {
  registrations: readonly TeamPlayerFinderRegistration[]
  selectedId: string | null
  onSelect: (teamRegistrationId: string) => void
}

/**
 * Which competition's ad the sub-tab is managing — one entry per team
 * registration, labelled by competition with the division beneath it.
 *
 * With a single entry there is no decision to make, so it renders as text: the
 * same rule the Team Budget roster picker follows on the same row.
 */
const PlayerFinderRegistrationPicker: React.FC<
  PlayerFinderRegistrationPickerProps
> = ({ registrations, selectedId, onSelect }) => {
  if (registrations.length <= 1) {
    return (
      <Text variant='paragraphSmall' weight='semiBold' color={colors.NEUTRAL_0}>
        {registrations[0]
          ? playerFinderRegistrationLabel(registrations[0])
          : ''}
      </Text>
    )
  }

  const options = registrations.map((entry) => ({
    value: entry.id,
    label: (
      <div css={optionStyle}>
        <span css={ellipsisStyle}>
          <Text variant='paragraphMedium' color={colors.NEUTRAL_0}>
            {entry.competition?.name || entry.division?.name || ''}
          </Text>
        </span>
        {entry.competition?.name && entry.division?.name ? (
          <Text variant='paragraphXSmall' color={colors.NEUTRAL_100}>
            {entry.division.name}
          </Text>
        ) : null}
      </div>
    ),
  }))

  return (
    <div css={pickerStyle} data-testid='player-finder-registration-picker'>
      <SelectInput
        label=''
        disabled={false}
        options={options}
        name='player-finder-registration'
        value={selectedId ?? ''}
        onChange={onSelect}
        fieldErrors={[]}
        customError={{}}
        allowClear={false}
        placeholder={capitalizeWords(
          balanceText('common:teamPlayerFinderPickerPlaceholder')
        )}
        style={selectStyle}
        backgroundColor='transparent'
      />
    </div>
  )
}

export default PlayerFinderRegistrationPicker

// ---- styled ----
// The compact width the Team Budget roster picker uses on the same scope row.
const PICKER_WIDTH = 300

const pickerStyle = css`
  display: flex;
  flex-direction: column;
  width: ${PICKER_WIDTH}px;
  max-width: 100%;
`

// Two lines (competition + division), at the scope row's shared height.
const selectStyle = css`
  width: ${PICKER_WIDTH}px;
  max-width: 100%;
  height: ${SCOPE_CONTROL_HEIGHT}px !important;

  .ant-select-selection-item {
    display: flex;
    align-items: center;
    min-width: 0;
  }
`

const optionStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const ellipsisStyle = css`
  display: block;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`
